import { useState, useEffect } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Package, Beef, Carrot, ChefHat, Snowflake, Flame, GripVertical, Plus, Edit2, Trash2, Check, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { RestaurantCategory } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

const iconMap: Record<string, any> = {
  Package, Beef, Carrot, ChefHat, Snowflake, Flame
};

const colorSchemes = [
  { value: 'amber', label: 'Amber', className: 'bg-amber-100 text-amber-800' },
  { value: 'red', label: 'Red', className: 'bg-red-100 text-red-800' },
  { value: 'green', label: 'Green', className: 'bg-green-100 text-green-800' },
  { value: 'orange', label: 'Orange', className: 'bg-orange-100 text-orange-800' },
  { value: 'blue', label: 'Blue', className: 'bg-blue-100 text-blue-800' },
  { value: 'purple', label: 'Purple', className: 'bg-purple-100 text-purple-800' },
  { value: 'gray', label: 'Gray', className: 'bg-gray-100 text-gray-800' },
  { value: 'pink', label: 'Pink', className: 'bg-pink-100 text-pink-800' },
  { value: 'indigo', label: 'Indigo', className: 'bg-indigo-100 text-indigo-800' },
  { value: 'teal', label: 'Teal', className: 'bg-teal-100 text-teal-800' }
];

interface SortableCategoryProps {
  category: RestaurantCategory;
  onEdit: (category: RestaurantCategory) => void;
  onDelete: (id: number) => void;
}

function SortableCategory({ category, onEdit, onDelete }: SortableCategoryProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = iconMap[category.icon || 'Package'] || Package;
  const colorScheme = colorSchemes.find(cs => cs.value === category.colorScheme) || colorSchemes[0];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-4 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-4">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="h-5 w-5" />
        </button>
        <div className={`p-2 rounded-lg ${colorScheme.className}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-medium text-gray-900">{category.name}</h3>
          <p className="text-sm text-gray-500">Slug: {category.slug}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 text-xs rounded-full ${category.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {category.active ? 'Active' : 'Inactive'}
        </span>
        <button
          onClick={() => onEdit(category)}
          className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
        >
          <Edit2 className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(category.id)}
          className="p-2 text-gray-600 hover:text-red-600 transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function RestaurantCategoriesManager() {
  const [categories, setCategories] = useState<RestaurantCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCategory, setEditingCategory] = useState<RestaurantCategory | null>(null);
  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    colorScheme: 'amber',
    icon: 'Package',
    active: true
  });
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/restaurant/categories/all', {
        headers: {
          'X-Session-Id': localStorage.getItem('sessionId') || ''
        }
      });
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      toast({ title: 'Failed to load categories', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex(cat => cat.id === active.id);
    const newIndex = categories.findIndex(cat => cat.id === over.id);
    
    const newCategories = arrayMove(categories, oldIndex, newIndex);
    setCategories(newCategories);

    // Update display order on server
    try {
      const categoryOrders = newCategories.map((cat, index) => ({
        id: cat.id,
        displayOrder: index
      }));

      const response = await fetch('/api/restaurant/categories/reorder', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': localStorage.getItem('sessionId') || '',
          'X-CSRF-Token': localStorage.getItem('csrfToken') || ''
        },
        body: JSON.stringify({ categoryOrders })
      });

      if (!response.ok) throw new Error('Failed to update order');
      toast({ title: 'Category order updated' });
    } catch (error) {
      toast({ title: 'Failed to update category order', variant: 'destructive' });
      fetchCategories(); // Revert on error
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = editingCategory 
        ? `/api/restaurant/categories/${editingCategory.id}`
        : '/api/restaurant/categories';
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': localStorage.getItem('sessionId') || '',
          'X-CSRF-Token': localStorage.getItem('csrfToken') || ''
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save category');
      }

      toast({ title: editingCategory ? 'Category updated' : 'Category created' });
      fetchCategories();
      resetForm();
    } catch (error: any) {
      toast({ title: error.message || 'Failed to save category', variant: 'destructive' });
    }
  };

  const handleEdit = (category: RestaurantCategory) => {
    setEditingCategory(category);
    setFormData({
      slug: category.slug,
      name: category.name,
      colorScheme: category.colorScheme,
      icon: category.icon || 'Package',
      active: category.active
    });
    setIsCreating(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this category? Products using this category will need to be reassigned.')) {
      return;
    }

    try {
      const response = await fetch(`/api/restaurant/categories/${id}`, {
        method: 'DELETE',
        headers: {
          'X-Session-Id': localStorage.getItem('sessionId') || '',
          'X-CSRF-Token': localStorage.getItem('csrfToken') || ''
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete category');
      }

      toast({ title: 'Category deleted' });
      fetchCategories();
    } catch (error: any) {
      toast({ title: error.message || 'Failed to delete category', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setIsCreating(false);
    setEditingCategory(null);
    setFormData({
      slug: '',
      name: '',
      colorScheme: 'amber',
      icon: 'Package',
      active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isCreating && (
        <Button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Category
        </Button>
      )}

      {isCreating && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingCategory ? 'Edit Category' : 'New Category'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="slug">Slug (URL-friendly ID)</Label>
                  <Input
                    id="slug"
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    placeholder="e.g., rice-noodles"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="name">Display Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Rice & Noodles"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="colorScheme">Color Scheme</Label>
                  <select
                    id="colorScheme"
                    value={formData.colorScheme}
                    onChange={(e) => setFormData({ ...formData, colorScheme: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {colorSchemes.map(cs => (
                      <option key={cs.value} value={cs.value}>{cs.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="icon">Icon</Label>
                  <select
                    id="icon"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Package">Package</option>
                    <option value="Beef">Beef</option>
                    <option value="Carrot">Carrot</option>
                    <option value="ChefHat">Chef Hat</option>
                    <option value="Snowflake">Snowflake</option>
                    <option value="Flame">Flame</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="active">Active</Label>
              </div>
              
              <div className="flex gap-2">
                <Button type="submit">
                  <Check className="h-4 w-4 mr-2" />
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={categories.map(cat => cat.id)}
            strategy={verticalListSortingStrategy}
          >
            {categories.map(category => (
              <SortableCategory
                key={category.id}
                category={category}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {categories.length === 0 && (
        <Card className="border-2 border-dashed">
          <CardContent className="text-center py-12">
            <p className="text-gray-500">No categories yet. Create your first category!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}